import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { SeverityBadge, StatusBadge, ConfidenceMeter } from "@/components/common/Badges";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ImpactEvidencePanel } from "@/components/workspaces/ImpactEvidencePanel";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const STATUSES = [
    { id: "all", label: "All" },
    { id: "open", label: "Awaiting review" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
];

export default function Recommendations() {
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState(searchParams.get("status") || "all");
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = status === "all" ? {} : { status };
            const { data } = await api.get("/recommendations", { params });
            setItems(data);
        } catch (e) {
            console.warn("Failed to load recommendations", e);
        }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const review = async (rid, action) => {
        try {
            const { data } = await api.post(`/recommendations/${rid}/review`, { action });
            const captured = data?.impact_evidence?.status === "captured";
            toast.success(
                `${action}d${captured ? " · Change Impact Brief attached" : ""}`,
            );
            await load();
            setOpen(data);
        } catch (e) { console.warn("Review failed", e); toast.error("Failed"); }
    };
    const explain = async (rid) => {
        try {
            toast.message("Consulting ASSEP reasoner…");
            const { data } = await api.post(`/recommendations/${rid}/explain`);
            if (data.explanation) {
                const fresh = (await api.get(`/recommendations/${rid}`)).data;
                setOpen(fresh);
                toast.success("Explanation updated");
            }
        } catch (e) { console.warn("Explain failed", e); toast.error("Failed"); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap" data-testid="recommendation-status-filter">
                {STATUSES.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`recommendation-status-${s.id}`}
                        aria-pressed={status === s.id}
                        onClick={() => setStatus(s.id)}
                        className={`px-3 py-1.5 rounded-sm border font-mono text-[10px] uppercase tracking-wider transition-all duration-300 ${
                            status === s.id
                                ? "border-cyan-400/50 text-cyan-300 bg-cyan-400/10"
                                : "border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/20"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    {items.length} recommendation{items.length === 1 ? "" : "s"}
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.length === 0 && (
                    <Panel className="md:col-span-2 xl:col-span-3" eyebrow="Empty" title="No recommendations available">
                        <p className="text-sm text-slate-400">
                            {status === "all"
                                ? "Open a workspace and run analysis to generate explainable engineering recommendations."
                                : `No recommendations with status “${status}”. Try a different filter.`}
                        </p>
                    </Panel>
                )}
                {items.map((r, i) => (
                    <motion.button
                        key={r.id}
                        data-testid={TID.recommendationCard(r.id)}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        whileHover={{ y: -2 }}
                        onClick={() => setOpen(r)}
                        className="panel-elevated text-left p-5 hover:border-cyan-500/30 transition-colors"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <SeverityBadge severity={r.risk_rating} />
                            <StatusBadge status={r.status} />
                        </div>
                        <h3 className="font-display text-base text-white">{r.title}</h3>
                        <p className="text-xs text-slate-400 mt-2 line-clamp-3">{r.root_cause}</p>
                        <div className="flex items-center justify-between mt-4">
                            <ConfidenceMeter value={r.confidence_score} />
                            <div className="text-[10px] font-mono uppercase text-slate-500">Effort · {r.estimated_effort}</div>
                        </div>
                    </motion.button>
                ))}
            </div>

            <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
                <SheetContent className="bg-[var(--bg-elevated)] border-l border-cyan-500/20 text-slate-200 w-full sm:max-w-2xl overflow-y-auto">
                    {open && (
                        <div>
                            <SheetHeader>
                                <div className="flex items-center gap-2 mb-2">
                                    <SeverityBadge severity={open.risk_rating} />
                                    <StatusBadge status={open.status} />
                                </div>
                                <SheetTitle className="font-display text-white text-xl">{open.title}</SheetTitle>
                                <SheetDescription className="text-slate-400">Explainable recommendation by ASSEP</SheetDescription>
                            </SheetHeader>

                            <div className="mt-6 space-y-5">
                                <Sec label="Root Cause"><p className="text-sm text-slate-300">{open.root_cause}</p></Sec>
                                <Sec label="Engineering Explanation"><p className="text-sm text-slate-300 whitespace-pre-wrap">{open.explanation}</p></Sec>
                                <Sec label="Rationale"><p className="text-sm text-slate-300">{open.rationale}</p></Sec>
                                <Sec label="Remediation">
                                    <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
                                        {open.remediation_steps?.map((s, i) => <li key={i}>{s}</li>)}
                                    </ol>
                                </Sec>
                                {open.impact_evidence && (
                                    <Sec label="Change Impact Evidence">
                                        <ImpactEvidencePanel rec={open} onUpdated={(fresh) => { setOpen(fresh); load(); }} />
                                    </Sec>
                                )}
                                <div className="flex gap-2 pt-4 border-t border-white/5">
                                    <button data-testid={TID.recommendationApprove(open.id)} onClick={() => review(open.id, "approve")} className="btn-primary">Approve</button>
                                    <button data-testid={TID.recommendationReject(open.id)} onClick={() => review(open.id, "reject")} className="btn-ghost text-rose-300 border-rose-500/30">Reject</button>
                                    <button data-testid={TID.recommendationExplain(open.id)} onClick={() => explain(open.id)} className="btn-ghost ml-auto">AI Explain</button>
                                </div>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}

function Sec({ label, children }) {
    return (
        <div>
            <div className="label-eyebrow mb-2">{label}</div>
            {children}
        </div>
    );
}
